import { createContext, useContext, useMemo, PropsWithChildren, useEffect } from 'react';
import { featureToggles } from 'config';
import { z } from 'zod';
import useQueryParameter from 'hooks/useQueryParameter';
import { parseJsonString } from 'utils/parseJsonString';
import availableFeatures, { AvailableFeatures } from 'constants/availableFeatures';

const context = createContext<AvailableFeatures[]>([]);

const { Provider } = context;
const { sessionStorage } = window;

const filterFeatures = (features: string[]) =>
  availableFeatures.filter((feature) => features.includes(feature));

const featureFlagSchema = z.array(z.string());

const parseFeatureFlags = parseJsonString(featureFlagSchema, []);

export const getFeatureFlag = (feature: AvailableFeatures) => {
  const featureIsEnabledInEnv = featureToggles[feature];
  const sessionStorageFeatures = parseFeatureFlags(sessionStorage.getItem('featureFlags'));
  const featureEnabledViaSessionStorage = sessionStorageFeatures.includes(feature);
  return featureIsEnabledInEnv || featureEnabledViaSessionStorage;
};

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const featuresQueryString = useQueryParameter('ft');

  const features = useMemo(() => {
    if (featuresQueryString) {
      return filterFeatures(featuresQueryString.split(','));
    }

    const item = sessionStorage.getItem('featureFlags');
    return filterFeatures(parseFeatureFlags(item));
  }, [featuresQueryString]);

  useEffect(() => {
    sessionStorage.setItem('featureFlags', JSON.stringify(features));
  }, [features]);

  return <Provider value={features}>{children}</Provider>;
};

export const useFeatureFlag = (feature: AvailableFeatures) => {
  const features = useContext(context);

  return useMemo(() => {
    const featureIsEnabledInEnv = featureToggles[feature];
    const featureEnabledViaQueryParam = features.includes(feature);

    return featureIsEnabledInEnv || featureEnabledViaQueryParam;
  }, [feature, features]);
};
